import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import type { TurnOperation } from '../../../contracts/turn-identity';
import { projectChatSubmitAtom } from '../agentRecordingCommand';
import type { SubmitProjectTurnArgs } from '../submit-project-turn-args';

/**
 * Publish the project chat's owner enqueue (for as long as the chat is mounted)
 * so surfaces outside the composer — the session-artifact transcription
 * controls — can route a turn THROUGH the queue. The agent then runs
 * ui.session_recording, so the action both drives the recorder and shows as a
 * row in the transcript. Cleared on unmount so a stale submit can't fire into
 * an unmounted chat.
 */
export const usePublishProjectChatSubmit = (
  enqueueTurn: (command: SubmitProjectTurnArgs) => TurnOperation,
  enabled = true
) => {
  const setProjectChatSubmit = useSetAtom(projectChatSubmitAtom);
  useEffect(() => {
    if (!enabled) {
      setProjectChatSubmit(null);
      return;
    }
    // Fire-and-forget at the atom boundary, as before: failures already toast
    // nowhere on this path, and an uncaught rejection would be new noise.
    setProjectChatSubmit(() => (text: string) => {
      void enqueueTurn({ text, failureUxOwner: 'caller' }).accepted.catch(
        () => undefined
      );
    });
    return () => setProjectChatSubmit(null);
  }, [enabled, enqueueTurn, setProjectChatSubmit]);
};
